/** @see https://open.er-api.com/v6/latest/USD */
export const OPEN_ER_USD_FX_URL = 'https://open.er-api.com/v6/latest/USD';

const FETCH_TIMEOUT_MS = 8000;
const FALLBACK_TTL_MS = 60 * 60 * 1000;

let memoryRates = null;
let memoryExpiresAt = 0;
let inflight = null;

/**
 * @param {object | null | undefined} payload
 * @returns {{ rates: Record<string, number>, expiresAt: number }}
 */
export function parseOpenErUsdRates(payload) {
  if (
    !payload ||
    payload.result !== 'success' ||
    !payload.rates ||
    typeof payload.rates !== 'object'
  ) {
    return { rates: {}, expiresAt: 0 };
  }
  const rates = {};
  for (const [ccy, value] of Object.entries(payload.rates)) {
    const code = String(ccy || '')
      .trim()
      .toUpperCase();
    const n = Number(value);
    if (!code || !Number.isFinite(n) || n <= 0) continue;
    rates[code] = n;
  }
  const nextUnix = Number(payload.time_next_update_unix);
  const expiresAt =
    Number.isFinite(nextUnix) && nextUnix > 0 ? nextUnix * 1000 : Date.now() + FALLBACK_TTL_MS;
  return { rates, expiresAt };
}

export function peekOpenErUsdRates() {
  return memoryRates && typeof memoryRates === 'object' ? memoryRates : {};
}

export async function ensureOpenErUsdRates({ force = false } = {}) {
  const now = Date.now();
  if (!force && memoryRates && now < memoryExpiresAt) return memoryRates;
  if (inflight) return inflight;

  inflight = (async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      const res = await fetch(OPEN_ER_USD_FX_URL, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return memoryRates || {};
      const parsed = parseOpenErUsdRates(await res.json());
      if (Object.keys(parsed.rates).length) {
        memoryRates = parsed.rates;
        memoryExpiresAt = parsed.expiresAt;
      }
      return memoryRates || {};
    } catch {
      return memoryRates || {};
    } finally {
      clearTimeout(timer);
    }
  })().finally(() => {
    inflight = null;
  });

  return inflight;
}
